import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, Save, Loader2, Trash2, Upload, Sparkles, Eye, EyeOff,
  FolderTree, Image as ImageIcon, Tag, Search, Menu
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { RichEditor } from "@/components/RichEditor";
import { API } from "@/lib/api";

const ICON_OPTIONS = [
  'Package', 'Folder', 'FolderTree', 'Archive', 'Box', 'Briefcase', 'ShoppingBag',
  'Printer', 'Pen', 'Pencil', 'Scissors', 'Paperclip', 'Clipboard', 'FileText',
  'BookOpen', 'GraduationCap', 'Monitor', 'Calculator', 'Palette', 'Heart',
  'Star', 'Zap', 'Gift', 'Tag', 'Layers', 'Grid', 'Mail', 'Phone',
  'MapPin', 'Globe', 'Shield', 'Key', 'Settings', 'Tool', 'Truck',
  'Home', 'Building', 'Users', 'Camera', 'Music', 'Coffee', 'Lightbulb',
];

export default function AdminCategoryEdit() {
  const { categoryId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isNew = categoryId === 'nova';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [children, setChildren] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: '', description: '', active: 1, id_parent: 2,
    icon: '', image_url: '', show_in_menu: false, position: 0,
    meta_title: '', meta_description: '', meta_keywords: ''
  });

  useEffect(() => {
    const loads = [
      axios.get(`${API}/admin/categories-flat`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
    ];
    if (!isNew) {
      loads.push(axios.get(`${API}/admin/categories/${categoryId}`, { headers: { Authorization: `Bearer ${token}` } }));
    }
    Promise.all(loads).then(([catsRes, catRes]) => {
      setAllCategories(catsRes.data);
      if (catRes?.data) {
        setForm(f => ({ ...f, ...catRes.data }));
        setChildren(catRes.data.children || []);
        setProductCount(catRes.data.product_count || 0);
      }
    }).catch(() => toast.error('Chyba nacitani'))
      .finally(() => setLoading(false));
  }, [categoryId, isNew, token]);

  const up = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Zadejte nazev'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, active: form.active,
        id_parent: form.id_parent, icon: form.icon, image_url: form.image_url,
        show_in_menu: form.show_in_menu, position: form.position,
        meta_title: form.meta_title, meta_description: form.meta_description,
        meta_keywords: form.meta_keywords,
      };
      if (isNew) {
        const res = await axios.post(`${API}/admin/categories`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Kategorie vytvorena');
        navigate(`/admin/kategorie/${res.data.id_category}`);
      } else {
        await axios.put(`${API}/admin/categories/${categoryId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Kategorie ulozena');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba ukladani'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/categories/${categoryId}/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      up('image_url', res.data.image_url);
      toast.success('Obrazek nahran');
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba nahravani'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deleteCategory = async () => {
    if (!window.confirm('Opravdu smazat kategorii? Tuto akci nelze vratit.')) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kategorie smazana');
      navigate('/admin/kategorie');
    } catch (err) { toast.error(err.response?.data?.detail || 'Nelze smazat'); }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/admin/products/ai-generate`, {
        product_name: `Kategorie: ${form.name}`,
        generate_type: 'all',
        category_name: form.name,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.description) up('description', res.data.description);
      if (res.data.meta_title) up('meta_title', res.data.meta_title);
      if (res.data.meta_description) up('meta_description', res.data.meta_description);
      if (res.data.meta_keywords) up('meta_keywords', res.data.meta_keywords);
      toast.success('AI text vygenerovan');
    } catch { toast.error('AI generovani selhalo'); }
    finally { setAiLoading(false); }
  };

  // Build parent tree for select
  const buildParentOptions = (cats, depth = 0) => {
    let opts = [];
    const sorted = cats.filter(c => c.id_parent === 2 || c.level_depth === 2).sort((a, b) => (a.position || 0) - (b.position || 0));
    const addLevel = (parentId, d) => {
      const children = cats.filter(c => c.id_parent === parentId).sort((a, b) => (a.position || 0) - (b.position || 0));
      for (const c of children) {
        if (c.id_category !== parseInt(categoryId)) {
          opts.push({ id: c.id_category, name: c.name, depth: d });
          addLevel(c.id_category, d + 1);
        }
      }
    };
    addLevel(2, 0);
    return opts;
  };
  const parentOptions = buildParentOptions(allCategories);

  if (loading) return <AdminLayout active="categories"><div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div></AdminLayout>;

  const inputCls = "w-full h-11 border border-slate-300 rounded px-4 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none";
  const labelCls = "text-xs font-bold uppercase text-slate-500 mb-1 block";

  return (
    <AdminLayout active="categories">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/kategorie')} className="text-slate-500 hover:text-[#004E98]" data-testid="back-to-categories"><ChevronLeft className="w-6 h-6" /></button>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="category-edit-title">{isNew ? 'Nova kategorie' : `Upravit: ${form.name}`}</h1>
        </div>
        {!isNew && (
          <button onClick={deleteCategory} className="h-10 px-4 border border-red-300 text-red-600 rounded text-sm flex items-center gap-2 hover:bg-red-50" data-testid="delete-category-btn">
            <Trash2 className="w-4 h-4" /> Smazat
          </button>
        )}
      </div>

      <form onSubmit={handleSave} className="max-w-5xl space-y-6">
        {/* Basic Info */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><FolderTree className="w-5 h-5" /> Zakladni informace</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelCls}>Nazev kategorie *</label>
              <input type="text" required value={form.name} onChange={e => up('name', e.target.value)} className={inputCls} data-testid="category-name-input" />
            </div>
            <div>
              <label className={labelCls}>Nadrazena kategorie</label>
              <select value={form.id_parent} onChange={e => up('id_parent', parseInt(e.target.value))} className={inputCls} data-testid="category-parent-select">
                <option value={2}>-- Korenova kategorie --</option>
                {parentOptions.map(o => (
                  <option key={o.id} value={o.id}>{'— '.repeat(o.depth)}{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Pozice (razeni)</label>
              <input type="number" value={form.position || 0} onChange={e => up('position', parseInt(e.target.value) || 0)} className={inputCls} data-testid="category-position-input" />
            </div>
            <div className="flex items-center gap-6 pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active === 1} onChange={e => up('active', e.target.checked ? 1 : 0)} className="w-4 h-4 text-[#004E98] rounded" />
                <span className="text-sm font-medium flex items-center gap-1">{form.active ? <Eye className="w-4 h-4 text-emerald-500" /> : <EyeOff className="w-4 h-4 text-slate-400" />} Aktivni</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.show_in_menu || false} onChange={e => up('show_in_menu', e.target.checked)} className="w-4 h-4 text-[#FF6B35] rounded" />
                <span className="text-sm font-medium flex items-center gap-1"><Menu className="w-4 h-4" /> Zobrazit v hlavnim menu</span>
              </label>
            </div>
            {!isNew && (
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>ID: <strong>{categoryId}</strong></span>
                <span>Podkategorii: <strong>{children.length}</strong></span>
                <span>Produktu: <strong>{productCount}</strong></span>
              </div>
            )}
          </div>
        </div>

        {/* Icon & Image */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Ikona a obrazek</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Icon picker */}
            <div>
              <label className={labelCls}>Ikona (lucide)</label>
              <div className="flex items-center gap-3 mb-2">
                <input type="text" value={form.icon || ''} onChange={e => up('icon', e.target.value)} placeholder="nazev ikony (napr. Package)" className={inputCls} data-testid="category-icon-input" />
                <button type="button" onClick={() => setShowIconPicker(!showIconPicker)} className="h-11 px-4 border border-slate-300 rounded text-sm hover:bg-slate-50 whitespace-nowrap" data-testid="icon-picker-btn">
                  {showIconPicker ? 'Zavrit' : 'Vybrat'}
                </button>
              </div>
              {showIconPicker && (
                <div className="grid grid-cols-8 gap-1 p-3 bg-slate-50 border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                  {ICON_OPTIONS.map(icon => (
                    <button key={icon} type="button" onClick={() => { up('icon', icon); setShowIconPicker(false); }}
                      className={`p-2 rounded text-xs text-center hover:bg-white hover:shadow-sm transition-all ${form.icon === icon ? 'bg-[#004E98] text-white' : 'text-slate-600'}`}>
                      {icon}
                    </button>
                  ))}
                </div>
              )}
              {form.icon && <p className="text-xs text-slate-400 mt-1">Vybrano: <strong>{form.icon}</strong></p>}
            </div>

            {/* Image upload */}
            <div>
              <label className={labelCls}>Obrazek kategorie</label>
              {form.image_url ? (
                <div className="relative group w-32 h-32 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 mb-2">
                  <img src={form.image_url} alt="" className="w-full h-full object-contain p-1" />
                  <button type="button" onClick={() => up('image_url', '')} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={isNew || uploading}
                  className="h-10 px-4 border border-slate-300 rounded text-sm flex items-center gap-2 hover:bg-slate-50 disabled:opacity-50" data-testid="category-image-upload-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {form.image_url ? 'Zmenit' : 'Nahrat'} obrazek
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                {isNew && <span className="text-xs text-slate-400">Nejdriv ulozte kategorii</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Description with WYSIWYG */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">Popis kategorie</h2>
            {form.name && (
              <button type="button" onClick={generateAI} disabled={aiLoading}
                className="h-8 px-3 bg-purple-600 text-white rounded text-xs font-medium flex items-center gap-1 hover:bg-purple-700 disabled:opacity-50" data-testid="ai-category-btn">
                {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI generovat texty
              </button>
            )}
          </div>
          <RichEditor value={form.description || ''} onChange={val => up('description', val)} minHeight="150px" />
        </div>

        {/* SEO Metadata */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Search className="w-5 h-5" /> SEO metadata</h2>
          <div className="space-y-4">
            <div><label className={labelCls}>Meta Title <span className="text-slate-400 font-normal">(max 60 znaku)</span></label>
              <input type="text" value={form.meta_title || ''} onChange={e => up('meta_title', e.target.value)} maxLength={70} className={inputCls} data-testid="cat-meta-title" />
              <div className="text-xs text-slate-400 mt-1">{(form.meta_title || '').length}/60</div>
            </div>
            <div><label className={labelCls}>Meta Description <span className="text-slate-400 font-normal">(max 160 znaku)</span></label>
              <textarea value={form.meta_description || ''} onChange={e => up('meta_description', e.target.value)} maxLength={170} rows={2}
                className="w-full border border-slate-300 rounded p-3 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none" data-testid="cat-meta-description" />
              <div className="text-xs text-slate-400 mt-1">{(form.meta_description || '').length}/160</div>
            </div>
            <div><label className={labelCls}>Klicova slova</label>
              <input type="text" value={form.meta_keywords || ''} onChange={e => up('meta_keywords', e.target.value)} className={inputCls} placeholder="klicove slovo 1, klicove slovo 2, ..." data-testid="cat-meta-keywords" /></div>
          </div>
          {(form.meta_title || form.name) && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase font-bold mb-2">Nahled ve vyhledavaci</p>
              <div className="text-[#1a0dab] text-lg font-medium truncate">{form.meta_title || form.name}</div>
              <div className="text-[#006621] text-sm truncate">example.com/kategorie/{categoryId}</div>
              <div className="text-sm text-slate-600 line-clamp-2">{form.meta_description || form.description?.replace(/<[^>]*>/g, '')?.slice(0, 160) || ''}</div>
            </div>
          )}
        </div>

        {/* Subcategories */}
        {!isNew && children.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-semibold text-slate-800 mb-4">Podkategorie ({children.length})</h2>
            <div className="space-y-1">
              {children.map(child => (
                <div key={child.id_category} className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FolderTree className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium">{child.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${child.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                      {child.active ? 'Aktivni' : 'Neaktivni'}
                    </span>
                  </div>
                  <button type="button" onClick={() => navigate(`/admin/kategorie/${child.id_category}`)}
                    className="text-sm text-[#004E98] hover:underline">Upravit</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex items-center gap-3 pb-8">
          <button type="submit" disabled={saving} className="h-11 px-6 bg-[#004E98] text-white rounded font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50" data-testid="save-category-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isNew ? 'Vytvorit kategorii' : 'Ulozit zmeny'}
          </button>
          <button type="button" onClick={() => navigate('/admin/kategorie')} className="h-11 px-6 border border-slate-300 rounded text-sm hover:bg-slate-50">Zrusit</button>
        </div>
      </form>
    </AdminLayout>
  );
}
